import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useNotify,
  useTableReset,
} from 'core/hooks';
import Table from 'core/components/VirtualTable';
import Section from 'views/EMR/Settings/BillingConfigurations/Section';
import {
  commonTableClasses,
} from 'views/EMR/Settings/BillingConfigurations';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  BillingProviderRuleDTO,
} from 'dtos';
import {
  billingProviderRulesColumns,
  BillingProviderSummary,
  PayerSummary,
  RuleSummary,
} from 'views/EMR/Settings/BillingConfigurations/BillingProviderRules/columns';
import BillingProviderRuleDialog, {
  BillingProviderRuleDialogProps,
} from 'views/EMR/Settings/BillingConfigurations/BillingProviderRules/Dialog';
import isNil from 'lodash/isNil';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  reorder,
} from 'utils/array';
import {
  useGetBillingProviderRules,
  useReorderBillingProviderRule,
} from 'views/EMR/Settings/hooks';
import DragNDropIcon from 'core/icons/dragNDrop';
import clsx from 'clsx';
import TableRow from 'core/components/VirtualTable/Row';
import map from 'lodash/map';
import last from 'lodash/last';

export default function BillingProviderRules() {
  const { data: billingProviderRules = [], refetch } = useGetBillingProviderRules();
  const { tableInstanceRef } = useTableReset();
  const userInfo = useUserInfo();

  const [
    billingProviderRuleDialogParams,
    setBillingProviderRuleDialogParams,
  ] = useState<BillingProviderRuleDialogProps | null>(null);

  useEffect(() => {
    const unsubscribeFromBillingProviderChange = settingsEventChannel.on('onBillingProviderRuleChange', () => {
      tableInstanceRef?.current?.refetch?.();
      refetch();
    });

    return () => {
      unsubscribeFromBillingProviderChange();
    };
  }, []);

  const {
    mutateAsync: reorderProviderRule,
  } = useReorderBillingProviderRule();

  const notify = useNotify();
  const onDragEnd = async (result: DropResult) => {
    if (!isNil(result.destination)) {
      const items = reorder(
        billingProviderRules,
        result.source.index,
        result.destination.index,
      );

      await reorderProviderRule({
        data: map(items, 'billingProviderRuleId'),
      });
      notify({
        variant: 'success',
        message: 'Billing Provider Rules reordered successfully.',
      });
      refetch();
    }
  };

  const getClonedRow = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric,
  ) => {
    const { draggableProps, dragHandleProps, innerRef } = provided;
    const rule = billingProviderRules[rubric.source.index];
    return (
      <div
        {...draggableProps}
        {...dragHandleProps}
        ref={innerRef}
        className="flex gap-2 items-center px-3 !max-h-[50px] opacity-90 bg-blue-50 border rounded !border-blue-500 text-xs"
      >
        <div className="flex gap-1">
          <DragNDropIcon className="!h-4 !w-4" />
          <div className="w-4 h-4rounded bg-gray-100 text-gray-700 text-center font-semibold">{(rule?.order ?? 0) + 1}</div>
        </div>
        <div className="flex-1">
          <PayerSummary {...rule} />
        </div>
        <div className="flex-1">
          <RuleSummary name={rule?.serivceFacilitySummary?.name} />
        </div>
        <div className="flex-1">
          <RuleSummary name={rule?.renderingProviderSummary?.name} />
        </div>
        <div className="flex-1">
          <BillingProviderSummary {...rule} />
        </div>
      </div>
    );
  };

  const columns = useMemo(() => (
    userInfo?.flags?.mayEditOrgConfiguration
      ? billingProviderRulesColumns
      : billingProviderRulesColumns.slice(0, -1)
  ), [userInfo]);

  const onAdd = useCallback(() => {
    const maxOrder = last(billingProviderRules)?.order ?? 0;
    setBillingProviderRuleDialogParams({
      billingProviderRule: null,
      defaultOrder: maxOrder + 1,
    });
  }, [billingProviderRules]);

  return (
    <>
      <Section
        headerProps={{
          title: 'Billing Provider Rules',
          onAdd: userInfo?.flags?.mayEditOrgConfiguration ? onAdd : undefined,
          onAddTooltip: 'Add Billing Provider Rule',
        }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId="billing-provider-rules-droppable"
            mode="virtual"
            renderClone={getClonedRow}
            isDropDisabled={!userInfo?.flags?.mayEditOrgConfiguration}
          >
            {(droppableProvided, droppableSnapshot) => (
              <div
                {...droppableProvided.droppableProps}
                ref={droppableProvided.innerRef}
                className="w-full"
              >
                <div className={clsx({ '!pb-8': droppableSnapshot?.isDraggingOver })}>
                  <Table<BillingProviderRuleDTO>
                    columns={columns}
                    data={billingProviderRules}
                    classes={{
                      ...commonTableClasses,
                      outerRow: clsx(commonTableClasses?.outerRow, {
                        'hover:!border-t-8 hover:!border-t-[#bfdbfe]': droppableSnapshot?.isDraggingOver,
                      }),
                    }}
                    renderRowComponent={(data) => {
                      const id = data?.original?.billingProviderRuleId?.toString() ?? '';
                      return (
                        <Draggable
                          key={id}
                          draggableId={id}
                          index={data.index}
                          isDragDisabled={!userInfo?.flags?.mayEditOrgConfiguration}
                        >
                          {(draggableProvided) => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                              style={{
                                ...draggableProvided.draggableProps.style,
                                transform: 'none',
                              }}
                            >
                              <TableRow<BillingProviderRuleDTO>
                                row={data}
                                className="!py-1 !px-2"
                                cellClassName={clsx('!items-start', commonTableClasses?.cell)}
                                onClick={(row) => {
                                  if (userInfo?.flags?.mayEditOrgConfiguration) {
                                    setBillingProviderRuleDialogParams({
                                      billingProviderRule: row?.original,
                                    });
                                  }
                                }}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    }}
                  />
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Section>
      {!isNil(billingProviderRuleDialogParams) && (
        <BillingProviderRuleDialog
          {...billingProviderRuleDialogParams}
          onClose={() => { setBillingProviderRuleDialogParams(null); }}
        />
      )}
    </>
  );
}
